<template>
    <div class="mt-4 mb-3">
        <form v-if="!authenticating" @submit.prevent="submitRequest">
            <div class="alert alert-brand">
                Här kan du hämta ditt bankkontonummer tryggt och direkt från din
                bank. Vänligen välj rätt bank i listan, fyll i personnummer och
                klicka "Visa konto" för att legitimera med BankID.
            </div>
            <div class="form-group">
                <label for="bank">Välj din bank*</label>
                <SbFormSelect
                    id="bank"
                    v-model="bank"
                    :options="billectaBanks"
                    value-field="code"
                    text-field="name"
                    required
                >
                    <template #first>
                        <SbFormSelectOption :value="null" disabled
                            >Välj bank</SbFormSelectOption
                        >
                    </template>
                </SbFormSelect>
            </div>

            <div class="form-group">
                <label for="ssn">Personnummer (YYYYMMDD-NNNN)*</label>
                <input
                    class="form-control"
                    id="ssn"
                    type="text"
                    v-model="ssn"
                    @blur.prevent="validateSSNInput"
                    minlength="13"
                    maxlength="13"
                    required
                />
                <div v-if="errorMessage" class="text-danger pt-2">
                    {{ errorMessage }}
                </div>
            </div>

            <div class="form-group text-center">
                <button
                    type="submit"
                    class="signatureComponentSignButton btn btn-secondary mt-3"
                >
                    Visa konton
                </button>
            </div>
        </form>

        <div v-else-if="!requestId">
            <div class="alert alert-brand">
                Häng kvar, vi anropar nu BankID. Det kan ta några sekunder.
            </div>
            <button
                id="spinner"
                type="button"
                class="signatureComponentSignButton btn btn-primary mt-3 wait"
            >
                <div class="spinner-border spinner-border-sm mr-2"></div>
            </button>
        </div>

        <template v-else>
            <div v-if="!isDone" class="text-center">
                <template v-if="!autoStartUrl && !currentQrToken">
                    <div class="alert alert-brand text-left">
                        <template v-if="billectaAuthenticationComplete">
                            Vi hämtar nu dina kontonummer. Vänligen vänta!
                        </template>
                        <template v-else>
                            Häng kvar, vi anropar nu BankID. Det kan ta några
                            sekunder.
                        </template>
                    </div>
                    <button
                        id="spinner"
                        type="button"
                        class="signatureComponentSignButton btn btn-primary mt-3 wait"
                    >
                        <div
                            class="spinner-border spinner-border-sm mr-2"
                        ></div>
                    </button>
                </template>

                <div v-if="autoStartUrl || currentQrToken" class="my-3">
                    <div class="alert alert-brand">
                        Nu kan du starta BankID. Efter det skall du tillbaka
                        till denna sidan för att välja konto.
                    </div>
                    <div v-if="autoStartUrl">
                        <a
                            :href="autoStartUrl"
                            target="_blank"
                            class="signatureComponentSignButton btn btn-primary w-full mb-3"
                        >
                            Öppna BankID på denna enhet
                        </a>
                    </div>

                    <template v-if="currentQrToken">
                        <p v-if="qrVisible">
                            Använd BankID-appen för att scanna bilden nedan:<br />
                            <img
                                :src="currentQrToken"
                                class="img-fluid my-5"
                                style="width: 175px"
                            />
                        </p>
                        <div v-else class="font-italic">
                            <button
                                @click.prevent="qrVisible = true"
                                v-if="!autoStartUrl"
                                class="signatureComponentSignButton btn btn-primary my-3 w-full"
                            >
                                Öppna BankID på en annan enhet
                            </button>
                            <small v-else
                                >Du kan även använda
                                <a
                                    href="#"
                                    class="text-color-profile-color"
                                    @click.prevent="qrVisible = true"
                                    >BankID på en annan enhet</a
                                ></small
                            >
                        </div>
                    </template>
                </div>
            </div>

            <div v-else-if="accounts.length > 0" class="list-group">
                <div class="alert alert-brand">
                    Välj vilket konto du vill använda:
                </div>
                <div class="form-group mt-3">
                    <SbFormSelect
                        id="bank"
                        v-model="selectedAccountIndex"
                        :options="displayAccounts"
                        required
                    >
                        <template #first>
                            <SbFormSelectOption :value="null" disabled
                                >Välj bankkonto</SbFormSelectOption
                            >
                        </template>
                    </SbFormSelect>
                </div>
                <div class="form-group text-center">
                    <button
                        class="signatureComponentSignButton btn btn-secondary my-3"
                        type="button"
                        @click.prevent="handlePrefill"
                        :disabled="selectedAccountIndex === null"
                    >
                        Använd detta konto
                    </button>
                </div>
                <div class="text-center mt-3">
                    <a type="button" @click.prevent="resetState"
                        ><i class="fal fa-rotate-right"></i> Starta om</a
                    >
                </div>
            </div>

            <div
                v-else
                class="text-center d-flex flex-column justify-content-center"
            >
                <div v-if="failed" class="alert alert-brand">
                    Oooops! Det verkar som att din bank inte kan lämna
                    kontouppgifterna just nu. Vänligen försök igen eller
                    fyll i dem manuellt!
                </div>
                <h6 v-else>Vi hittar tyvärr inget konto.</h6>
                <a href="#" @click.prevent="switchToManual">Fyll i manuellt!</a>
                <a class="mt-3" type="button" @click.prevent="resetState"
                    ><i class="fal fa-rotate-right"></i> Starta om</a
                >
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
#spinner {
    width: 100%;

    &.wait .spinner-border {
        vertical-align: middle;
    }
}
</style>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, toRefs } from "vue";
import axios from "axios";
import { DateTime, Settings } from "luxon";
import { v4 as uuid } from "uuid";
import { useActionLog } from "../../composables/useActionLog";
const props = defineProps({
  quoteKey: String
});
const {
  quoteKey
} = toRefs(props);
const emit = defineEmits(["switchToManual", "success", "failed"]);
const $actionLog = useActionLog();
const billectaBanks = ref([]);
const bank = ref(null);
const ssn = ref("");
const authenticating = ref(false);
const errorMessage = ref(null);
const requestId = ref(null);
const accounts = ref([]);
const selectedAccountIndex = ref(null);
const autoStartToken = ref(null);
const qrVisible = ref(false);
const currentQrToken = ref(null);
const billectaAuthenticationComplete = ref(false);
const failed = ref(false);
const isDone = ref(false);
const intervals = ref([]);
const autoStartUrl = computed(() => {
  if (!autoStartToken.value) {
    return null;
  }
  return `bankid:///?autostarttoken=${autoStartToken.value}&redirect=null`;
});
const displayAccounts = computed(() => {
  return accounts.value.map((account, index) => {
    return {
      value: index,
      text: `Namn: ${account.name}, Kontonummer: ${account.number}`
    };
  });
});
async function getBillectaBanks() {
  const URL = route("quotes.billecta.accountsRetrieval.getBanks", {
    key: quoteKey.value
  });
  const response = await axios.get(URL);
  return response.data;
}
function initialState() {
  return {
    billectaBanks: [],
    bank: null,
    ssn: "",
    authenticating: false,
    errorMessage: null,
    requestId: null,
    accounts: [],
    selectedAccountIndex: null,
    autoStartToken: null,
    qrVisible: false,
    currentQrToken: null,
    billectaAuthenticationComplete: false,
    failed: false,
    isDone: false,
    intervals: []
  };
}
async function resetState() {
  clearIntervals();
  const banks = billectaBanks.value;
  Object.assign($data, initialState());
  billectaBanks.value = banks;
}
function switchToManual() {
  emit("switchToManual");
}
function validateSSNInput(e) {
  let cleanedInput = ssn.value.replace(/[^0-9-]/g, "");
  if (cleanedInput.length < 10 || cleanedInput.length > 13) {
    ssn.value = cleanedInput;
    return;
  }

  // Split the date part and the numeric part
  let parts = cleanedInput.split("-");
  let datePart, numPart;
  if (parts.length > 1) {
    datePart = parts[0];
    numPart = parts[1];
  } else {
    const datePartLength = cleanedInput.length === 10 ? 6 : 8;
    datePart = cleanedInput.slice(0, datePartLength);
    numPart = cleanedInput.slice(datePartLength);
  }
  // Handle date part (assuming it's always in YYMMDD format)
  if (datePart.length >= 6) {
    Settings.twoDigitCutoffYear = new Date().getFullYear() - 15 - 2000;
    let yearFormat = datePart.length >= 8 ? "yyyy" : "yy";
    const dateObj = DateTime.fromFormat(datePart, `${yearFormat}MMdd`);
    if (dateObj.isValid) {
      datePart = dateObj.toFormat("yyyyMMdd");
    } else {
      console.log("Invalid date format.");
    }
  }

  // Combine the parts into the final formatted string
  ssn.value = `${datePart}-${numPart}`;
}
async function submitRequest() {
  if (authenticating.value) {
    return;
  }
  const regexPattern = /^(19[0-9]{2}|20[0-9]{2})(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])-\d{4}$/;
  // Test the input against the regex pattern
  if (!regexPattern.test(ssn.value)) {
    console.log("Invalid format");
    errorMessage.value = "Ogiltig inmatning. Kontrollera och skicka igen";
    return false;
  }
  authenticating.value = true;
  errorMessage.value = null;
  const payload = {
    bank: bank.value,
    ssn: ssn.value
  };
  try {
    const URL = route("quotes.billecta.accountsRetrieval.request", {
      key: quoteKey.value
    });
    const response = await axios.post(URL, payload);
    requestId.value = response.data["PublicId"];
    intervals.value.push(setInterval(pollRetrievalRequestStatus, 1000));
    $actionLog.logAISRequested(quoteKey.value, "Process för kontouppslagning startad");
  } catch (error) {
    errorMessage.value = "Ogiltig inmatning. Kontrollera och skicka igen";
    console.warn(error);
    authenticating.value = false;
  }
}
const handlePrefill = async function () {
  if (selectedAccountIndex.value === null) {
    console.log("Account not selected!");
    return;
  }
  let account = accounts.value[selectedAccountIndex.value];
  account.bank = bank.value;
  emit("success", account);
};
function clearIntervals() {
  intervals.value.forEach(clearInterval);
}
const pollRetrievalRequestStatus = async function () {
  if (isDone.value) {
    return;
  }
  try {
    const URL = route("quotes.billecta.accountsRetrieval.getRequest", {
      key: quoteKey.value,
      retrievalRequestId: requestId.value
    });
    const response = await axios.get(URL);
    if (isDone.value) {
      return;
    }
    switch (response.data["Status"]) {
      case "Waiting":
        currentQrToken.value = response.data["QR"];
        autoStartToken.value = response.data["BankIdAutostartToken"];
        const authenticationCompleted = response.data["failureCode"] === "STATE_AUTHENTICATION_COMPLETED";
        if (authenticationCompleted && !billectaAuthenticationComplete.value) {
          $actionLog.logAISAuthenticationCompleted(quoteKey.value);
        }
        billectaAuthenticationComplete.value = authenticationCompleted;
        break;
      case "Success":
        accounts.value = response.data["AccountNumbers"].map(account => {
          return {
            name: account.Name,
            number: account.AccountNo,
            clearing: account.ClearingNo
          };
        });
        isDone.value = true;
        clearIntervals();
        $actionLog.logAISAccountsRetrieved(quoteKey.value, "Hämtade och visas för mottagaren");
        break;
      default:
        failed.value = true;
        isDone.value = true;
        emit("failed");
        clearIntervals();
        // this.$actionLog.logAISAccountsRetrieved(
        //     this.quoteKey,
        //     "Misslyckades"
        // );
        break;
    }
  } catch (err) {
    if (!err.response || err.response.status !== 404) {
      throw err;
    }
  }
};
const $data = {
  set billectaBanks(value) {
    billectaBanks.value = value;
  },
  set bank(value) {
    bank.value = value;
  },
  set ssn(value) {
    ssn.value = value;
  },
  set authenticating(value) {
    authenticating.value = value;
  },
  set errorMessage(value) {
    errorMessage.value = value;
  },
  set requestId(value) {
    requestId.value = value;
  },
  set accounts(value) {
    accounts.value = value;
  },
  set selectedAccountIndex(value) {
    selectedAccountIndex.value = value;
  },
  set autoStartToken(value) {
    autoStartToken.value = value;
  },
  set qrVisible(value) {
    qrVisible.value = value;
  },
  set currentQrToken(value) {
    currentQrToken.value = value;
  },
  set billectaAuthenticationComplete(value) {
    billectaAuthenticationComplete.value = value;
  },
  set failed(value) {
    failed.value = value;
  },
  set isDone(value) {
    isDone.value = value;
  },
  set intervals(value) {
    intervals.value = value;
  }
};
onMounted(async () => {
  billectaBanks.value.push(...(await getBillectaBanks()));
});
onBeforeUnmount(() => {
  clearIntervals();
});
</script>
